/**
 * SlideOutPanel - Slide-in panel from the right side
 *
 * Usage:
 * <SlideOutPanel
 *   isOpen={showPanel}
 *   onClose={() => setShowPanel(false)}
 *   title="Panel Title"
 *   subtitle="Optional description"
 * >
 *   <p>Panel content here</p>
 * </SlideOutPanel>
 */

import { X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

import { cn } from '../utils';

/** Selector for focusable elements within panel */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface SlideOutPanelProps {
  /** Whether panel is open */
  isOpen: boolean;
  /** Called when panel should close */
  onClose: () => void;
  /** Panel title */
  title?: string;
  /** Optional subtitle/description below the title */
  subtitle?: string;
  /** Panel content */
  children: React.ReactNode;
  /** Footer content (buttons, etc.) - rendered in a separate fixed section at bottom */
  footer?: React.ReactNode;
  /** Size variant (width) */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Hide close button */
  hideCloseButton?: boolean;
  /** Additional classes for panel content */
  className?: string;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export function SlideOutPanel({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  hideCloseButton,
  className,
}: SlideOutPanelProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Handle focus trapping within the panel
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement) return;

      // If shift+tab from first element, wrap to last
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      }
      // If tab from last element, wrap to first
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    },
    [onClose]
  );

  // Use capture phase so this fires before any bubble-phase listeners (e.g., sidebar toggle)
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element to restore on close
      previousActiveElement.current = document.activeElement as HTMLElement;

      document.addEventListener('keydown', handleKeyDown, { capture: true });
      document.body.style.overflow = 'hidden';

      // Focus the first focusable element in the panel
      requestAnimationFrame(() => {
        const focusableElements =
          panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        focusableElements?.[0]?.focus();
      });
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.body.style.overflow = '';

      // Restore focus to the previously focused element
      if (previousActiveElement.current && !isOpen) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div ref={overlayRef} className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'panel-title' : undefined}
        className={cn(
          'relative flex h-full w-full flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-white/[0.08] dark:bg-[#18181b]',
          'animate-in slide-in-from-right duration-200',
          sizeClasses[size],
          className
        )}
      >
        {/* Header */}
        {(title || !hideCloseButton) && (
          <div className="flex flex-shrink-0 items-start justify-between border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4 dark:border-white/[0.06]">
            <div className="flex-1">
              {title && (
                <h2
                  id="panel-title"
                  className="text-base font-semibold text-gray-900 dark:text-white"
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">{subtitle}</p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="-mr-1 -mt-1 ml-4 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-zinc-500 dark:hover:bg-white/[0.06] dark:hover:text-white"
                aria-label="Close panel"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex flex-shrink-0 items-center justify-end gap-3 border-t border-gray-200 px-4 py-3 sm:px-5 sm:py-4 dark:border-white/[0.06]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
